import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import { css, html, nothing } from "lit";
import { property, query, state } from "lit/decorators.js";
import { msg } from "@lit/localize";
import "@progressive-development/pd-forms/pd-button";
import "@progressive-development/pd-forms/pd-input";
import { pdIcons } from "@progressive-development/pd-icon";
import "@progressive-development/pd-forms/pd-select";
import { classMap } from "lit/directives/class-map.js";
import { PdBaseUIInput } from "@progressive-development/pd-forms";
import "@progressive-development/pd-forms/pd-input-url";
//#region src/pd-markdown-input/PdMarkdownInput.ts
/**
* @license
* Copyright (c) 2024 PD Progressive Development UG. All rights reserved.
*/
var DEFAULT_ACTIONS = [
	"heading",
	"bold",
	"italic",
	"strike",
	"link",
	"bulletList",
	"orderedList",
	"blockquote",
	"code",
	"codeBlock",
	"horizontalRule"
];
/**
* Heading options for pd-select.
* Value maps directly to heading level (0 = paragraph).
*/
var HEADING_OPTIONS = [
	{
		name: "Absatz",
		value: "0"
	},
	{
		name: "H1",
		value: "1"
	},
	{
		name: "H2",
		value: "2"
	},
	{
		name: "H3",
		value: "3"
	},
	{
		name: "H4",
		value: "4"
	},
	{
		name: "H5",
		value: "5"
	}
];
/**
* Toolbar button groups for visual grouping.
* Each sub-array renders as a connected button group.
*/
var TOOLBAR_GROUPS = [
	[
		"bold",
		"italic",
		"strike"
	],
	["bulletList", "orderedList"],
	[
		"blockquote",
		"code",
		"codeBlock"
	],
	["link", "horizontalRule"]
];
var TOOLBAR_BUTTONS = [
	{
		action: "bold",
		label: "B",
		title: "Bold",
		run: (e) => e.chain().focus().toggleBold().run(),
		isActive: (e) => e.isActive("bold")
	},
	{
		action: "italic",
		label: "I",
		title: "Italic",
		run: (e) => e.chain().focus().toggleItalic().run(),
		isActive: (e) => e.isActive("italic")
	},
	{
		action: "strike",
		label: "S",
		title: "Strikethrough",
		run: (e) => e.chain().focus().toggleStrike().run(),
		isActive: (e) => e.isActive("strike")
	},
	{
		action: "bulletList",
		label: "•",
		title: "Bullet List",
		run: (e) => e.chain().focus().toggleBulletList().run(),
		isActive: (e) => e.isActive("bulletList")
	},
	{
		action: "orderedList",
		label: "1.",
		title: "Ordered List",
		run: (e) => e.chain().focus().toggleOrderedList().run(),
		isActive: (e) => e.isActive("orderedList")
	},
	{
		action: "blockquote",
		label: "",
		icon: pdIcons.ICON_BLOCKQUOTE,
		title: "Blockquote",
		run: (e) => e.chain().focus().toggleBlockquote().run(),
		isActive: (e) => e.isActive("blockquote")
	},
	{
		action: "code",
		label: "",
		icon: pdIcons.ICON_CODE,
		title: "Inline Code",
		run: (e) => e.chain().focus().toggleCode().run(),
		isActive: (e) => e.isActive("code")
	},
	{
		action: "codeBlock",
		label: "",
		icon: pdIcons.ICON_CODE_BLOCK,
		title: "Code Block",
		run: (e) => e.chain().focus().toggleCodeBlock().run(),
		isActive: (e) => e.isActive("codeBlock")
	},
	{
		action: "horizontalRule",
		label: "",
		icon: pdIcons.ICON_HORIZONTAL_RULE,
		title: "Horizontal Rule",
		run: (e) => e.chain().focus().setHorizontalRule().run(),
		isActive: () => false
	},
	{
		action: "link",
		label: "",
		icon: pdIcons.ICON_LINK,
		title: "Link",
		run: () => {},
		isActive: (e) => e.isActive("link")
	}
];
/**
* WYSIWYG Markdown input component based on TipTap.
*
* Renders a toolbar with formatting buttons above a rich-text editor.
* The component value is always a Markdown string.
* Integrates with pd-form-container via pd-form-element-change events.
*
* @tagname pd-markdown-input
* @summary WYSIWYG Markdown editor with toolbar and form integration.
*
* @event pd-form-element-change - Fired when the markdown value changes.
* @event pd-form-element-register - Fired on initialization for form container registration.
*
* @cssprop --pd-markdown-input-width - Editor width. Default: `100%`.
* @cssprop --pd-markdown-input-min-height - Minimum editor height. Default: `9rem`.
* @cssprop --pd-markdown-input-max-height - Maximum editor height; longer content scrolls inside the editor (toolbar stays visible). `none` restores unbounded growth. Default: `24rem`.
* @cssprop --pd-markdown-input-border - Editor border. Default: `1px solid var(--pd-default-light-col)`.
* @cssprop --pd-markdown-input-border-bottom - Editor bottom border accent. Default: `2px solid var(--pd-default-col)`.
* @cssprop --pd-markdown-input-border-radius - Border radius. Default: `var(--pd-radius-md)`.
* @cssprop --pd-markdown-input-bg-col - Editor background. Default: `var(--pd-default-bg-col)`.
* @cssprop --pd-markdown-input-font-size - Content font size. Default: `var(--pd-default-font-input-size)`.
* @cssprop --pd-markdown-input-toolbar-bg - Toolbar background. Default: `var(--pd-default-bg-light-col)`.
* @cssprop --pd-markdown-input-toolbar-border - Toolbar bottom border. Default: `1px solid var(--pd-default-light-col)`.
*/
var PdMarkdownInput = class extends PdBaseUIInput {
	constructor(..._args) {
		super(..._args);
		this.rows = 6;
		this.placeHolder = "";
		this.toolbarActions = DEFAULT_ACTIONS;
		this._tiptapInitialized = false;
		this._updatingFromEditor = false;
		this._activeStates = {};
		this._activeHeadingLevel = 0;
		this._linkPanel = null;
		this._onEditorScroll = () => {
			this._closeLinkPanel();
		};
		this._onDocPointerDown = (e) => {
			const path = e.composedPath();
			if (this._linkPanelEl && path.includes(this._linkPanelEl) || this._toolbarEl && path.includes(this._toolbarEl)) return;
			this._closeLinkPanel();
		};
	}
	static {
		this.styles = [PdBaseUIInput.styles, css`
      :host {
        display: block;
        --_width: var(--pd-markdown-input-width, 100%);
        --_min-height: var(--pd-markdown-input-min-height, 9rem);
        --_max-height: var(--pd-markdown-input-max-height, 24rem);
        --_border: var(
          --pd-markdown-input-border,
          1px solid var(--pd-default-light-col, #afc1d2)
        );
        --_border-bottom: var(
          --pd-markdown-input-border-bottom,
          2px solid var(--pd-default-col, #067394)
        );
        --_radius: var(
          --pd-markdown-input-border-radius,
          var(--pd-radius-md, 0.5rem)
        );
        --_bg: var(--pd-markdown-input-bg-col, var(--pd-default-bg-col, #fff));
        --_font-size: var(
          --pd-markdown-input-font-size,
          var(--pd-default-font-input-size, 1rem)
        );
        --_toolbar-bg: var(
          --pd-markdown-input-toolbar-bg,
          var(--pd-default-bg-light-col, #f2f9fa)
        );
        --_toolbar-border: var(
          --pd-markdown-input-toolbar-border,
          1px solid var(--pd-default-light-col, #afc1d2)
        );
      }

      /* Anker fürs Link-Popover — außerhalb des .md-container, weil der
       * mit overflow: hidden clippen würde. */
      .md-anchor {
        position: relative;
      }

      .md-container {
        width: var(--_width);
        border: var(--_border);
        border-bottom: var(--_border-bottom);
        border-radius: var(--_radius);
        overflow: hidden;
        background: var(--_bg);
        box-sizing: border-box;
      }

      .link-panel {
        position: absolute;
        z-index: 10;
        width: 320px;
        max-width: 100%;
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        padding: 0.75rem;
        background: var(--_bg);
        border: var(--_border);
        border-radius: var(--_radius);
        box-shadow: var(--pd-shadow-xl, 0 6px 16px rgba(0, 0, 0, 0.15));
      }

      /* Felder füllen die Panel-Breite (Default wäre 250px fix) — bündig
       * mit der rechten Kante der Aktions-Buttons. */
      .link-panel pd-input-url,
      .link-panel pd-input {
        --pd-input-field-width: 100%;
      }

      .link-panel-actions {
        display: flex;
        gap: 0.5rem;
        justify-content: flex-end;
      }

      /* Kompakte Icon-Buttons — gleiche Größe wie die Toolbar-Buttons,
       * damit die Aktionen nicht mit Dialog-Fußzeilen konkurrieren. */
      .link-panel-actions pd-button {
        --pd-button-height: 2rem;
        --pd-button-min-height: 2rem;
      }

      .md-container.focus {
        border: var(
          --pd-input-field-border-focus,
          2px solid var(--pd-default-col, #067394)
        );
        box-shadow: 0 0 0 1px var(--pd-default-col, #067394);
      }

      .md-container.error {
        border: 1px solid var(--pd-default-error-col);
        background-color: var(--pd-default-error-light-col);
      }

      .md-container.focus.error {
        border: 2px solid var(--pd-default-error-col);
      }

      .md-container.valid {
        border: 1px solid var(--pd-default-success-col);
        background-color: var(--pd-default-success-light-col);
      }

      .md-container.focus.valid {
        border: 2px solid var(--pd-default-success-col);
      }

      .md-container.disabled {
        opacity: 0.5;
        cursor: auto;
        background-color: var(--pd-input-field-bg-col-disabled, #e9e9e9);
      }

      /* Toolbar */
      .toolbar {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 0.375rem;
        padding: 0.375rem 0.5rem;
        background: var(--_toolbar-bg);
        border-bottom: var(--_toolbar-border);

        /* Compact pd-button sizing — match select height */
        --pd-button-height: 2rem;
        --pd-button-min-height: 2rem;
        --pd-button-font-size: 0.85rem;
        --pd-button-padding: 0 0.4rem;
        --pd-button-min-width: 2rem;
        --pd-button-border-radius: 0;

        /* Solid transparent buttons instead of dashed outline */
        --pd-button-bg-col: transparent;
        --pd-button-border-col: var(--pd-default-light-col, #afc1d2);
        --pd-button-font-col: var(--pd-default-font-col, #3c3c3b);
      }

      /* Visual button group */
      .btn-group {
        display: inline-flex;
      }

      /* Heading select — subtle, same weight as buttons */
      .toolbar pd-select {
        --pd-input-field-width: 5.5rem;
        --pd-input-field-height: 2rem;
        --pd-input-field-padding: 0.15rem 0.25rem;
        --pd-input-field-border: 1px solid var(--pd-default-light-col, #afc1d2);
        --pd-input-field-border-bottom: 1px solid
          var(--pd-default-light-col, #afc1d2);
        --pd-input-field-bg-col: transparent;
        --pd-radius-md: 0;
      }

      .toolbar-separator {
        width: 1px;
        height: 1.5rem;
        flex-shrink: 0;
        align-self: center;
        margin: 0 0.25rem;
        background: var(--pd-default-light-col, #afc1d2);
      }

      /* Editor area */
      .editor-host {
        min-height: var(--_min-height);
        /* Mitwachsen bis zum Deckel, danach scrollt der Inhalt — die
         * Toolbar liegt außerhalb und bleibt dabei sichtbar. */
        max-height: var(--_max-height);
        overflow-y: auto;
        padding: 0.5rem 0.75rem;
        font-size: var(--_font-size);
        font-family: var(--pd-default-font-input-family, inherit);
        color: var(--pd-default-font-input-col, inherit);
        box-sizing: border-box;
      }

      /* ProseMirror base styles (injected manually for Shadow DOM) */
      .editor-host .ProseMirror {
        outline: none;
        word-wrap: break-word;
        white-space: pre-wrap;
        -webkit-font-variant-ligatures: none;
        font-variant-ligatures: none;
        min-height: inherit;
      }

      .editor-host .ProseMirror:focus {
        outline: none;
      }

      .editor-host .ProseMirror p {
        margin: 0 0 0.4em;
      }

      .editor-host .ProseMirror h1,
      .editor-host .ProseMirror h2,
      .editor-host .ProseMirror h3,
      .editor-host .ProseMirror h4,
      .editor-host .ProseMirror h5 {
        margin: 0.6em 0 0.3em;
        line-height: 1.3;
      }

      .editor-host .ProseMirror ul,
      .editor-host .ProseMirror ol {
        margin: 0.3em 0;
        padding-left: 1.5em;
      }

      .editor-host .ProseMirror li {
        margin-bottom: 0.15em;
      }

      .editor-host .ProseMirror code {
        background: var(--pd-default-bg-light-col, #f2f9fa);
        border-radius: 3px;
        padding: 0.15em 0.3em;
        font-size: 0.9em;
        font-family: "Fira Code", "Consolas", monospace;
      }

      .editor-host .ProseMirror pre {
        background: var(--pd-default-bg-light-col, #f2f9fa);
        border: 1px solid var(--pd-default-light-col, #afc1d2);
        border-radius: var(--pd-radius-sm, 0.25rem);
        padding: 0.75em;
        overflow-x: auto;
      }

      .editor-host .ProseMirror pre code {
        background: transparent;
        padding: 0;
      }

      .editor-host .ProseMirror blockquote {
        border-left: 3px solid var(--pd-default-col, #067394);
        margin: 0.5em 0;
        padding-left: 0.75em;
        color: var(--pd-default-font-muted-col, #6b7280);
      }

      .editor-host .ProseMirror a {
        color: var(--pd-default-font-link-col, #067394);
        text-decoration: underline;
      }

      .editor-host .ProseMirror hr {
        border: none;
        border-top: 1px solid var(--pd-default-light-col, #afc1d2);
        margin: 1em 0;
      }

      /* Placeholder */
      .editor-host .ProseMirror p.is-editor-empty:first-child::before {
        content: attr(data-placeholder);
        float: left;
        color: var(--pd-default-font-muted-col, #adb5bd);
        pointer-events: none;
        height: 0;
      }
    `];
	}
	connectedCallback() {
		super.connectedCallback();
	}
	async firstUpdated() {
		await this._initTiptap();
		super.firstUpdated();
	}
	disconnectedCallback() {
		this._closeLinkPanel();
		this._destroyTiptap();
		super.disconnectedCallback();
	}
	update(changedProps) {
		super.update(changedProps);
		if (this._editor && !this._updatingFromEditor) {
			if (changedProps.has("initValue") && this._phase === "ready") {
				const currentMd = this._editor.getMarkdown();
				if (this._value !== currentMd) this._editor.commands.setContent(this._value, {
					emitUpdate: false,
					contentType: "markdown"
				});
			}
		}
		if (this._editor && changedProps.has("disabled")) this._editor.setEditable(!this.disabled && !this.readonly);
		if (this._editor && changedProps.has("readonly")) this._editor.setEditable(!this.disabled && !this.readonly);
	}
	render() {
		const inputId = `${this.id || "md"}Editor`;
		const errorId = `${this.id || "md"}Error`;
		const containerClasses = {
			"md-container": true,
			focus: this._focused,
			error: this._invalid,
			valid: this._valid && !!this._value,
			disabled: this.disabled
		};
		const showHeadingSelect = this.toolbarActions.includes("heading");
		return html`
      ${this._renderLabel(inputId)}
      <div class="md-anchor">
        <div class="${classMap(containerClasses)}">
          ${!this.disabled && !this.readonly ? html`
                  <div class="toolbar" role="toolbar" aria-label="Formatting">
                    ${showHeadingSelect ? html`
                            <pd-select
                              no-label
                              internalElement
                              .values="${HEADING_OPTIONS}"
                              .initValue="${String(this._activeHeadingLevel)}"
                              @pd-form-element-change="${this._onHeadingChange}"
                              @pd-form-element-register="${this._stopEvent}"
                            ></pd-select>
                            <div class="toolbar-separator"></div>
                          ` : nothing}
                    ${this._renderToolbarGroups()}
                  </div>
                ` : nothing}
          <div
            class="editor-host"
            id="${inputId}"
            style="min-height: ${this.rows * 1.5}rem"
          ></div>
        </div>
        ${this._linkPanel ? this._renderLinkPanel() : nothing}
      </div>
      ${this._renderErrorMsg(errorId)}
    `;
	}
	/**
	* An der Selektion verankertes Link-Popover (ersetzt window.prompt).
	* Liegt außerhalb des .md-container (overflow: hidden) im .md-anchor.
	* Die pd-form-element-*-Events der inneren Inputs bleiben in der
	* Komponente (Formular-Grenze, Muster pd-rating-input).
	*/
	_renderLinkPanel() {
		const panel = this._linkPanel;
		return html`
      <div
        class="link-panel"
        style="top: ${panel.top}px; left: ${panel.left}px"
        @keydown="${this._onLinkPanelKeyDown}"
        @pd-form-element-register="${this._stopEvent}"
        @pd-form-element-change="${this._stopEvent}"
        @pd-form-element-blur="${this._stopEvent}"
        @pd-form-element-focus="${this._stopEvent}"
      >
        <pd-input-url
          id="linkUrlId"
          required
          valueName="__pdmi_link_url"
          .label="${msg("Link", { id: "pd.markdown.link.url" })}"
          .initValue="${panel.href}"
        ></pd-input-url>
        <pd-input
          id="linkTextId"
          valueName="__pdmi_link_text"
          .label="${msg("Anzeige-Text", { id: "pd.markdown.link.text" })}"
          .initValue="${panel.text}"
        ></pd-input>
        <div class="link-panel-actions">
          ${panel.href ? html`
                  <pd-button
                    size="sm"
                    text=""
                    icon="${pdIcons.ICON_DELETE_NEW}"
                    title="${msg("Entfernen", { id: "pd.markdown.link.remove" })}"
                    iconLabel="${msg("Entfernen", { id: "pd.markdown.link.remove" })}"
                    @click="${this._removeLink}"
                  ></pd-button>
                ` : nothing}
          <pd-button
            size="sm"
            text=""
            icon="${pdIcons.ICON_CANCEL}"
            title="${msg("Abbrechen", { id: "pd.markdown.link.cancel" })}"
            iconLabel="${msg("Abbrechen", { id: "pd.markdown.link.cancel" })}"
            @click="${this._cancelLinkPanel}"
          ></pd-button>
          <pd-button
            size="sm"
            primary
            text=""
            icon="${pdIcons.ICON_CHECK}"
            title="${msg("Übernehmen", { id: "pd.markdown.link.apply" })}"
            iconLabel="${msg("Übernehmen", { id: "pd.markdown.link.apply" })}"
            @click="${this._applyLink}"
          ></pd-button>
        </div>
      </div>
    `;
	}
	_renderToolbarGroups() {
		const parts = [];
		for (let i = 0; i < TOOLBAR_GROUPS.length; i++) {
			const visibleBtns = TOOLBAR_GROUPS[i].filter((action) => this.toolbarActions.includes(action)).map((action) => TOOLBAR_BUTTONS.find((b) => b.action === action)).filter(Boolean);
			if (visibleBtns.length === 0) continue;
			if (parts.length > 0) parts.push(html`<div class="toolbar-separator"></div>`);
			parts.push(html`
        <div class="btn-group">
          ${visibleBtns.map((btn) => html`
              <pd-button
                text="${btn.label}"
                icon="${btn.icon || ""}"
                title="${btn.title}"
                ?selected="${this._activeStates[btn.action] ?? false}"
                ?disabled="${this.disabled}"
                @button-clicked="${() => this._onToolbarClick(btn)}"
              ></pd-button>
            `)}
        </div>
      `);
		}
		return parts;
	}
	/** Returns HTML representation of the current content for parsedValue in events. */
	_getParsedValue() {
		return this._editor ? this._editor.getHTML() : this._value;
	}
	async _initTiptap() {
		if (this._tiptapInitialized || !this._editorHost) return;
		this._tiptapInitialized = true;
		const { createTiptapEditor } = await import("./tiptap-setup.js");
		this._editor = createTiptapEditor({
			element: this._editorHost,
			content: this._value || "",
			placeholder: this.placeHolder,
			editable: !this.disabled && !this.readonly,
			onUpdate: (markdown) => this._onEditorUpdate(markdown),
			onFocus: () => this._onEditorFocus(),
			onBlur: () => this._onEditorBlur(),
			onTransaction: (editor) => this._onEditorTransaction(editor)
		});
	}
	_destroyTiptap() {
		if (this._editor) {
			this._editor.destroy();
			this._editor = void 0;
			this._tiptapInitialized = false;
		}
	}
	_onEditorUpdate(markdown) {
		this._updatingFromEditor = true;
		this._handleChangedValue(markdown, void 0, true);
		this._updatingFromEditor = false;
	}
	_onEditorFocus() {
		this._focused = true;
		this.dispatchEvent(new CustomEvent("pd-form-element-focus", {
			detail: {
				name: this.valueName || this.id || "field",
				value: this._value
			},
			bubbles: true,
			composed: true
		}));
	}
	_onEditorBlur() {
		this._focused = false;
		this._touched = true;
		this.dispatchEvent(new CustomEvent("pd-form-element-blur", {
			detail: {
				name: this.valueName || this.id || "field",
				value: this._value
			},
			bubbles: true,
			composed: true
		}));
	}
	_onEditorTransaction(editor) {
		const newStates = {};
		for (const btn of TOOLBAR_BUTTONS) newStates[btn.action] = btn.isActive(editor);
		this._activeStates = newStates;
		let headingLevel = 0;
		for (let level = 1; level <= 5; level++) if (editor.isActive("heading", { level })) {
			headingLevel = level;
			break;
		}
		this._activeHeadingLevel = headingLevel;
	}
	_onToolbarClick(btn) {
		if (!this._editor) return;
		if (btn.action === "link") {
			this._toggleLinkPanel();
			return;
		}
		btn.run(this._editor);
	}
	_toggleLinkPanel() {
		if (this._linkPanel) {
			this._cancelLinkPanel();
			return;
		}
		const editor = this._editor;
		if (!editor) return;
		if (editor.isActive("link")) editor.chain().extendMarkRange("link").run();
		const { from, to } = editor.state.selection;
		const href = editor.getAttributes("link")?.href ?? "";
		const text = editor.state.doc.textBetween(from, to, "\n");
		const anchorRect = this._panelAnchor.getBoundingClientRect();
		const coordsFrom = editor.view.coordsAtPos(from);
		const coordsTo = editor.view.coordsAtPos(to);
		const left = Math.max(0, Math.min(coordsFrom.left - anchorRect.left, anchorRect.width - 320));
		const top = coordsTo.bottom - anchorRect.top + 6;
		this._linkPanel = {
			from,
			to,
			top,
			left,
			href,
			text
		};
		this._editorHost?.addEventListener("scroll", this._onEditorScroll);
		document.addEventListener("pointerdown", this._onDocPointerDown);
	}
	_closeLinkPanel() {
		this._linkPanel = null;
		this._editorHost?.removeEventListener("scroll", this._onEditorScroll);
		document.removeEventListener("pointerdown", this._onDocPointerDown);
	}
	_cancelLinkPanel() {
		this._closeLinkPanel();
		this._editor?.commands.focus();
	}
	_onLinkPanelKeyDown(e) {
		if (e.key === "Escape") {
			e.stopPropagation();
			this._cancelLinkPanel();
		}
	}
	/**
	* Setzt/aktualisiert den Link über der gemerkten Range. Geänderter
	* Anzeige-Text ersetzt die Selektion; ohne Selektion und ohne Text
	* wird die URL selbst als Text eingefügt. Invalide/leere URLs blockt
	* die pd-input-url-Validierung mit Inline-Fehler.
	*/
	async _applyLink() {
		const editor = this._editor;
		const panel = this._linkPanel;
		const urlInput = this._linkUrlInput;
		if (!editor || !panel || !urlInput) return;
		const url = (urlInput.value ?? "").trim();
		if (!url) {
			urlInput.errorMsg = msg("Gültige URL eingeben (z.\xA0B. example.com)", { id: "pd.form.field.invalid.url" });
			return;
		}
		if (!await urlInput.runValidators()) return;
		const { from, to } = panel;
		const oldText = editor.state.doc.textBetween(from, to, "\n");
		const text = (this._linkTextInput?.value ?? "").trim() || url;
		if (text !== oldText) editor.chain().focus().insertContentAt({
			from,
			to
		}, [{
			type: "text",
			text,
			marks: [{
				type: "link",
				attrs: { href: url }
			}]
		}]).run();
		else editor.chain().focus().setTextSelection({
			from,
			to
		}).extendMarkRange("link").setLink({ href: url }).setTextSelection(to).run();
		this._closeLinkPanel();
	}
	_removeLink() {
		const editor = this._editor;
		const panel = this._linkPanel;
		if (!editor || !panel) return;
		editor.chain().focus().setTextSelection({
			from: panel.from,
			to: panel.to
		}).extendMarkRange("link").unsetLink().run();
		this._closeLinkPanel();
	}
	_onHeadingChange(e) {
		e.stopPropagation();
		if (!this._editor) return;
		const level = parseInt(e.detail.value, 10);
		if (level === 0) this._editor.chain().focus().setParagraph().run();
		else this._editor.chain().focus().toggleHeading({ level }).run();
	}
	_stopEvent(e) {
		e.stopPropagation();
	}
};
__decorate([property({ type: Number })], PdMarkdownInput.prototype, "rows", void 0);
__decorate([property({ type: String })], PdMarkdownInput.prototype, "placeHolder", void 0);
__decorate([property({
	type: Array,
	attribute: false
})], PdMarkdownInput.prototype, "toolbarActions", void 0);
__decorate([query(".editor-host")], PdMarkdownInput.prototype, "_editorHost", void 0);
__decorate([state()], PdMarkdownInput.prototype, "_activeStates", void 0);
__decorate([state()], PdMarkdownInput.prototype, "_activeHeadingLevel", void 0);
__decorate([state()], PdMarkdownInput.prototype, "_linkPanel", void 0);
__decorate([query(".md-anchor")], PdMarkdownInput.prototype, "_panelAnchor", void 0);
__decorate([query(".link-panel")], PdMarkdownInput.prototype, "_linkPanelEl", void 0);
__decorate([query(".toolbar")], PdMarkdownInput.prototype, "_toolbarEl", void 0);
__decorate([query("#linkUrlId")], PdMarkdownInput.prototype, "_linkUrlInput", void 0);
__decorate([query("#linkTextId")], PdMarkdownInput.prototype, "_linkTextInput", void 0);
//#endregion
export { PdMarkdownInput };
